import React from 'react';

import Drawer from '@mui/material/Drawer';
import { useDispatch } from 'react-redux';

import Button from 'components/Button';
import FollowSpeaker from 'components/FollowSpeaker';
import IconFemtasy from 'components/IconFemtasy';
import Message from 'components/Message';
import SpeakerTag from 'components/SpeakerTag';
import Typography from 'components/Typography';

import { StoryDetailsFragment } from 'graphql/generated/graphql';

import { useAuth } from 'hooks/useAuth';
import { useTracking } from 'hooks/useTracking';

import { showBanner } from 'store/modules/banner';
import { openAddStoryToPlaylist } from 'store/modules/playlist';
import { addToQueue } from 'store/modules/queue';

import { useUrls } from 'constants/urls';
import breakpoints from 'styles/base/breakpoints.module.scss';
import gaps from 'styles/base/gaps_exported.module.scss';
import palette from 'styles/base/palette.module.scss';

import './style.scss';

import FirstTimeTooltip from './FirstTimeTooltip';

export interface IProps {
  actionMenuIconRef: React.RefObject<HTMLDivElement>;
  story: StoryDetailsFragment | null | undefined;
  open: boolean;
  onClose: () => void;
}

const ActionsMenu = ({ actionMenuIconRef, story, open, onClose }: IProps) => {
  const dispatch = useDispatch();
  const { trackContentInteraction } = useTracking();
  const { isAuthenticated } = useAuth();
  const { pricingPage } = useUrls();

  if (!story) {
    return null;
  }

  const handleAddToQueue = () => {
    trackContentInteraction(
      'click',
      'modals',
      'story-details-action-add-to-queue',
    );
    dispatch(
      showBanner({
        message: { id: 'SM0123.Queue.Success.msg1' },
        displaySeconds: 4,
      }),
    );
    dispatch(addToQueue(story));
    onClose();
  };

  const handleOnClose = () => {
    trackContentInteraction('close', 'modals', 'story-details-actions-menu');
    onClose();
  };

  const storyLoved = story.loved || story.playlists.length > 0;

  const handleAddToPlaylist = () => {
    trackContentInteraction(
      'click',
      'modals',
      `story-${story.id}-details-action-add-to-playlist`,
    );
    trackContentInteraction(
      storyLoved ? 'unfollow' : 'follow',
      'story-details-actions-menu',
      story.id,
    );
    dispatch(openAddStoryToPlaylist(story));
    onClose();
  };

  return (
    <>
      <FirstTimeTooltip anchorEl={actionMenuIconRef} />
      <Drawer
        className="ActionsMenu"
        anchor="top"
        open={open}
        onClose={handleOnClose}
        role="menu"
        PaperProps={{
          sx: {
            backgroundColor: palette.colorSoftSensualAudios,
            padding: gaps.gap24,
            maxWidth: breakpoints.md,
            marginLeft: 'auto',
            marginRight: 'auto',
            borderRadius: `0 0 ${gaps.gap16} ${gaps.gap16}`,
            boxShadow: 'none',
          },
        }}
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(42, 22, 19, 0.85)',
          },
        }}
      >
        {!isAuthenticated && (
          <div className="ActionsMenu__cta">
            <Button
              linkTo={pricingPage}
              title={{ id: 'components.StoryDetails.ActionsMenu.unlock' }}
              icon={{
                component: (
                  <IconFemtasy icon="Lockline" size="24" color="white" />
                ),
              }}
            />
          </div>
        )}

        <div className="ActionsMenu__modal">
          <div className="ActionsMenu__container">
            <Typography className="ActionsMenu__actions_title" variant="h2">
              <Message message={story.title} />
              <IconFemtasy
                className="ActionsMenu__actions_cancel"
                onClick={handleOnClose}
                icon="Cancel"
              />
            </Typography>

            <div className="ActionsMenu__actions">
              <div className="action" onClick={handleAddToQueue}>
                <span className="action-icon">
                  <IconFemtasy icon="Add-to-queue" size="16" />
                </span>
                <Typography variant="body">
                  <Message message={{ id: 'SM0123.CTA.Add to Queue' }} />
                </Typography>
              </div>

              <div className="action" onClick={handleAddToPlaylist}>
                <span className="action-icon">
                  <IconFemtasy
                    icon={storyLoved ? 'Heartsolid' : 'Heartline'}
                    size="16"
                  />
                </span>
                <Typography variant="body">
                  <Message
                    message={{
                      id: storyLoved
                        ? 'components.StoryDetails.ActionsMenu.SavedToPlaylist'
                        : 'components.StoryDetails.ActionsMenu.SaveToPlaylist',
                    }}
                  />
                </Typography>
              </div>

              <div className="action">
                <span className="action-icon">
                  <FollowSpeaker
                    speaker={story.speaker}
                    matomoName="story-details-actions-menu"
                    iconSize="16"
                    message={
                      <Typography variant="body">
                        <Message
                          message={{
                            id: story.speaker.loved
                              ? 'components.StoryDetails.ActionsMenu.UnfollowSpeaker'
                              : 'components.StoryDetails.ActionsMenu.FollowSpeaker',
                          }}
                        />
                      </Typography>
                    }
                  />
                </span>

                <SpeakerTag speaker={story.speaker} />
              </div>
            </div>
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default ActionsMenu;
